// src/components/player/PlayerCareerTab.js — Mockup C: Highlighted Table + YoY Strip
import React from 'react';
import { formatBattingAvg, formatERA } from '../../services/statFormatUtils';
import { computeTotals as engineComputeTotals } from '../../services/tables/statTotals';

// ─── Style Constants (matches Overview tab panel styling) ───
const S = {
  panel: {
    background: 'rgba(23,23,23,0.5)',
    borderRadius: 5,
    border: '1px solid rgba(55,65,81,0.5)',
    overflow: 'hidden',
  },
  panelHdr: {
    padding: '6px 12px 5px',
    borderBottom: '1px solid rgba(55,65,81,0.2)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
  },
  label: {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: 1.1,
    textTransform: 'uppercase',
    color: '#eab308',
  },
  sub: { fontSize: 9, color: '#404040' },
  tableScroll: {
    maxHeight: 360,
    overflowY: 'auto',
    overflowX: 'auto',
  },
  th: {
    fontSize: 11,
    fontWeight: 600,
    color: '#525252',
    padding: '6px 8px',
    textAlign: 'center',
    background: 'rgba(23,23,23,0.95)',
    borderBottom: '1px solid rgba(55,65,81,0.35)',
    whiteSpace: 'nowrap',
  },
  td: {
    fontSize: 12,
    fontWeight: 400,
    color: '#d4d4d4',
    padding: '5px 8px',
    textAlign: 'center',
    borderBottom: '1px solid rgba(55,65,81,0.08)',
    whiteSpace: 'nowrap',
  },
  frozenYear: { left: 0, width: 52, position: 'sticky', zIndex: 3, textAlign: 'left', paddingLeft: 14 },
  frozenTm:   { left: 52, width: 44, position: 'sticky', zIndex: 3, textAlign: 'left', paddingLeft: 8 },
  best: { color: '#10b981', fontWeight: 600 },
  totals: {
    color: '#eab308',
    fontWeight: 600,
    borderTop: '1px solid rgba(234,179,8,0.3)',
    borderBottom: 'none',
    background: 'rgba(20,20,20,0.97)',
  },
};

// ─── Column Definitions ───
const HITTER_COLS = [
  { key: 'g',   title: 'G',   get: r => r?.hitting?.games_played ?? 0, fmt: v => v, sum: true },
  { key: 'ab',  title: 'AB',  get: r => r?.hitting?.at_bats ?? 0,      fmt: v => v, sum: true },
  { key: 'r',   title: 'R',   get: r => r?.hitting?.runs ?? 0,         fmt: v => v, sum: true },
  { key: 'h',   title: 'H',   get: r => r?.hitting?.hits ?? 0,         fmt: v => v, sum: true },
  { key: '2b',  title: '2B',  get: r => r?.hitting?.doubles ?? 0,      fmt: v => v, sum: true },
  { key: '3b',  title: '3B',  get: r => r?.hitting?.triples ?? 0,      fmt: v => v, sum: true },
  { key: 'hr',  title: 'HR',  get: r => r?.hitting?.home_runs ?? 0,    fmt: v => v, sum: true },
  { key: 'rbi', title: 'RBI', get: r => r?.hitting?.rbi ?? 0,          fmt: v => v, sum: true },
  { key: 'sb',  title: 'SB',  get: r => r?.hitting?.stolen_bases ?? 0, fmt: v => v, sum: true },
  { key: 'bb',  title: 'BB',  get: r => r?.hitting?.walks ?? 0,        fmt: v => v, sum: true },
  { key: 'so',  title: 'SO',  get: r => r?.hitting?.strikeouts ?? 0,   fmt: v => v, sum: true },
  { key: 'avg', title: 'AVG', get: r => r?.hitting?.batting_avg ?? 0,  fmt: v => formatBattingAvg(v), rate: true, higher: true },
  { key: 'obp', title: 'OBP', get: r => r?.hitting?.obp ?? 0,         fmt: v => formatBattingAvg(v), rate: true, higher: true },
  { key: 'slg', title: 'SLG', get: r => r?.hitting?.slg ?? 0,         fmt: v => formatBattingAvg(v), rate: true, higher: true },
  { key: 'ops', title: 'OPS', get: r => r?.hitting?.ops ?? 0,         fmt: v => formatBattingAvg(v), rate: true, higher: true },
];

const PITCHER_COLS = [
  { key: 'g',   title: 'G',    get: r => r?.pitching?.games_played ?? 0,      fmt: v => v, sum: true },
  { key: 'gs',  title: 'GS',   get: r => r?.pitching?.games_started ?? 0,     fmt: v => v, sum: true },
  { key: 'ip',  title: 'IP',   get: r => r?.pitching?.innings_pitched ?? 0,   fmt: v => parseFloat(v).toFixed(1), sum: true },
  { key: 'era', title: 'ERA',  get: r => r?.pitching?.era ?? 0,               fmt: v => formatERA(v), rate: true, higher: false },
  { key: 'whip',title: 'WHIP', get: r => r?.pitching?.whip ?? 0,              fmt: v => formatBattingAvg(v), rate: true, higher: false },
  { key: 'w',   title: 'W',    get: r => r?.pitching?.wins ?? 0,              fmt: v => v, sum: true },
  { key: 'l',   title: 'L',    get: r => r?.pitching?.losses ?? 0,            fmt: v => v, sum: true },
  { key: 'sv',  title: 'SV',   get: r => r?.pitching?.saves ?? 0,             fmt: v => v, sum: true },
  { key: 'qs',  title: 'QS',   get: r => r?.pitching?.quality_starts ?? 0,    fmt: v => v, sum: true },
  { key: 'so',  title: 'SO',   get: r => r?.pitching?.strikeouts_pitched ?? 0,fmt: v => v, sum: true },
  { key: 'ha',  title: 'H',    get: r => r?.pitching?.hits_allowed ?? 0,      fmt: v => v, sum: true },
  { key: 'bba', title: 'BB',   get: r => r?.pitching?.walks_allowed ?? 0,     fmt: v => v, sum: true },
];

// ─── Fantasy-relevant categories for YoY strip ───
const HITTER_YOY = ['avg', 'ops', 'hr', 'rbi', 'r', 'sb'];
const PITCHER_YOY = ['era', 'whip', 'w', 'so', 'qs', 'sv'];

// ─── Helpers ───

// Compute career bests per column (returns Set of "colKey-seasonIdx" for highlighting)
const computeBests = (seasons, cols) => {
  const bests = new Set();
  cols.forEach(col => {
    if (col.rate && seasons.length === 0) return;
    // Filter to seasons with meaningful playing time
    const qualified = seasons.filter(s => {
      const g = s?.hitting?.games_played ?? s?.pitching?.games_played ?? 0;
      return g >= 20;
    });
    if (qualified.length === 0) return;

    let bestVal = col.higher === false ? Infinity : -Infinity;
    let bestIdxs = [];

    seasons.forEach((s, i) => {
      const g = s?.hitting?.games_played ?? s?.pitching?.games_played ?? 0;
      if (g < 20) return; // skip short stints
      const v = col.get(s);
      if (v === 0 && col.rate) return; // skip zero rates
      if (col.higher === false) {
        // Lower is better (ERA, WHIP)
        if (v < bestVal) { bestVal = v; bestIdxs = [i]; }
        else if (v === bestVal) bestIdxs.push(i);
      } else {
        // Higher is better (everything else)
        if (v > bestVal) { bestVal = v; bestIdxs = [i]; }
        else if (v === bestVal) bestIdxs.push(i);
      }
    });
    bestIdxs.forEach(idx => bests.add(`${col.key}-${idx}`));
  });
  return bests;
};

// Map short column keys → canonical component leaves the SSoT engine emits.
const HITTER_KEYMAP = {
  g: 'games_played', ab: 'at_bats', r: 'runs', h: 'hits', '2b': 'doubles',
  '3b': 'triples', hr: 'home_runs', rbi: 'rbi', sb: 'stolen_bases', bb: 'walks',
  so: 'strikeouts', avg: 'batting_avg', obp: 'obp', slg: 'slg', ops: 'ops',
};
const PITCHER_KEYMAP = {
  g: 'games_played', gs: 'games_started', ip: 'innings_pitched', era: 'era',
  whip: 'whip', w: 'wins', l: 'losses', sv: 'saves', qs: 'quality_starts',
  so: 'strikeouts_pitched', ha: 'hits_allowed', bba: 'walks_allowed',
};

// Career totals row via the SSoT engine (statTotals.computeTotals) — sums components
// and recomputes rates. Fixes the prior bespoke OBP which omitted HBP/SF (hard-coded
// 0). Mirrors the mobile StatsTabContent career-totals path (live since 77d99121).
const computeTotals = (seasons, cols, isPitcher) => {
  const flat = engineComputeTotals(
    seasons.map(s => (isPitcher ? s.pitching : s.hitting) || {}),
    { statType: isPitcher ? 'pitching' : 'hitting' },
  );
  const map = isPitcher ? PITCHER_KEYMAP : HITTER_KEYMAP;
  const totals = {};
  Object.entries(map).forEach(([shortKey, leaf]) => { totals[shortKey] = flat[leaf]; });
  return totals;
};

// Determine YoY color
const yoyColor = (val, col) => {
  if (val === 0 || val === null || isNaN(val)) return '#737373';
  if (col.higher === false) return val < 0 ? '#10b981' : '#ef4444';
  return val > 0 ? '#10b981' : '#ef4444';
};

// ─── Main Component ───
const PlayerCareerTab = ({ player, isPitcher }) => {
  const career_stats = player?.career_stats || [];

  if (!career_stats || career_stats.length === 0) {
    return (
      <div style={{ padding: '32px 16px', textAlign: 'center' }}>
        <p style={{ color: '#737373', fontSize: 13 }}>No historical career statistics available</p>
      </div>
    );
  }

  // Sort oldest → newest
  const sorted = [...career_stats].sort((a, b) => (a.season || 0) - (b.season || 0));
  const cols = isPitcher ? PITCHER_COLS : HITTER_COLS;
  const yoyCols = isPitcher ? PITCHER_YOY : HITTER_YOY;
  const bests = computeBests(sorted, cols);
  const totals = computeTotals(sorted, cols, isPitcher);

  // ─── YoY: compare last two full seasons ───
  const fullSeasons = sorted.filter(s => {
    const g = s?.hitting?.games_played ?? s?.pitching?.games_played ?? 0;
    return g >= 30;
  });
  const hasTwoSeasons = fullSeasons.length >= 2;
  const prev = hasTwoSeasons ? fullSeasons[fullSeasons.length - 2] : null;
  const curr = hasTwoSeasons ? fullSeasons[fullSeasons.length - 1] : null;

  // Build YoY data
  const yoyData = yoyCols.map(colKey => {
    const col = cols.find(c => c.key === colKey);
    if (!col || !prev || !curr) return null;
    const prevVal = col.get(prev);
    const currVal = col.get(curr);
    const delta = currVal - prevVal;
    return {
      col,
      prevVal,
      currVal,
      delta,
      prevSeason: prev.season,
      currSeason: curr.season,
    };
  }).filter(Boolean);

  // ─── Scrollbar CSS ───
  const scrollbarCSS = `
    .career-table-scroll::-webkit-scrollbar { width: 6px; height: 6px; }
    .career-table-scroll::-webkit-scrollbar-track { background: rgba(23,23,23,0.3); }
    .career-table-scroll::-webkit-scrollbar-thumb { background: rgba(234,179,8,0.3); border-radius: 3px; }
    .career-table-scroll::-webkit-scrollbar-thumb:hover { background: rgba(234,179,8,0.5); }
  `;

  return (
    <div style={{ padding: '8px 16px 14px' }}>
      <style>{scrollbarCSS}</style>

      {/* ═══ CAREER TABLE ═══ */}
      <div style={S.panel}>
        <div style={S.panelHdr}>
          <span style={S.label}>Career Statistics</span>
          <span style={S.sub}>{sorted.length} seasons · Best values highlighted</span>
        </div>

        <div className="career-table-scroll" style={S.tableScroll}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: isPitcher ? 740 : 820 }}>
            <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
              <tr>
                <th style={{ ...S.th, ...S.frozenYear, background: 'rgba(23,23,23,0.95)' }}>Year</th>
                <th style={{ ...S.th, ...S.frozenTm, background: 'rgba(23,23,23,0.95)' }}>Tm</th>
                {cols.map(c => (
                  <th key={c.key} style={S.th}>{c.title}</th>
                ))}
              </tr>
            </thead>

            <tbody>
              {sorted.map((season, idx) => (
                <tr key={season.season || idx}>
                  <td style={{ ...S.td, ...S.frozenYear, color: '#fff', fontWeight: 500, background: 'rgba(23,23,23,0.9)' }}>
                    {season.season || '—'}
                  </td>
                  <td style={{ ...S.td, ...S.frozenTm, color: '#737373', background: 'rgba(23,23,23,0.9)' }}>
                    {season.mlb_team || '—'}
                  </td>
                  {cols.map(c => {
                    const val = c.get(season);
                    const isBest = bests.has(`${c.key}-${idx}`);
                    return (
                      <td key={c.key} style={{ ...S.td, ...(isBest ? S.best : {}) }}>
                        {c.fmt(val)}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>

            {/* Pinned totals row */}
            <tfoot style={{ position: 'sticky', bottom: 0, zIndex: 2 }}>
              <tr>
                <td style={{ ...S.td, ...S.totals, ...S.frozenYear, background: 'rgba(20,20,20,0.97)' }}>TOTAL</td>
                <td style={{ ...S.td, ...S.totals, ...S.frozenTm, background: 'rgba(20,20,20,0.97)' }}>—</td>
                {cols.map(c => (
                  <td key={c.key} style={{ ...S.td, ...S.totals }}>
                    {c.rate
                      ? c.fmt(totals[c.key] || 0)
                      : c.key === 'ip'
                        ? parseFloat(totals[c.key] || 0).toFixed(1)
                        : Math.round(totals[c.key] || 0)
                    }
                  </td>
                ))}
              </tr>
            </tfoot>
          </table>
        </div>

        {/* Legend */}
        <div style={{ padding: '4px 12px 5px', display: 'flex', gap: 14, alignItems: 'center', borderTop: '1px solid rgba(55,65,81,0.1)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 9, color: '#404040' }}>
            <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#10b981' }} />
            Career best
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 9, color: '#404040' }}>
            <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#eab308' }} />
            Career totals (pinned)
          </div>
        </div>
      </div>

      {/* ═══ YoY DELTA STRIP ═══ */}
      {hasTwoSeasons && yoyData.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${yoyData.length}, 1fr)`, gap: 6, marginTop: 6 }}>
          {yoyData.map(({ col, prevVal, currVal, delta, prevSeason, currSeason }) => {
            const color = yoyColor(delta, col);
            const isFlat = Math.abs(delta) < 0.0005 && col.rate;
            return (
              <div key={col.key} style={S.panel}>
                <div style={{ padding: '8px 10px', textAlign: 'center' }}>
                  {/* Category label */}
                  <div style={{ fontSize: 9, color: '#525252', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    {col.title}
                  </div>

                  {/* Previous → Current values */}
                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'baseline', gap: 6, margin: '4px 0 2px' }}>
                    <span style={{ fontSize: 11, color: '#525252' }}>{col.fmt(prevVal)}</span>
                    <span style={{ fontSize: 10, color: isFlat ? '#737373' : color }}>→</span>
                    <span style={{ fontSize: 13, fontWeight: 600, color: isFlat ? '#737373' : color }}>{col.fmt(currVal)}</span>
                  </div>

                  {/* Delta */}
                  <div style={{ fontSize: 10, fontWeight: 600, color: isFlat ? '#737373' : color }}>
                    {col.rate
                      ? (() => {
                          const d = delta;
                          const sign = d > 0 ? '+' : d < 0 ? '−' : '';
                          const absD = Math.abs(d);
                          if (col.key === 'era') return `${sign}${absD.toFixed(2)}`;
                          return `${sign}${formatBattingAvg(absD)}`;
                        })()
                      : `${delta > 0 ? '+' : delta < 0 ? '−' : ''}${Math.abs(delta)}`
                    }
                  </div>

                  {/* Season labels */}
                  <div style={{ fontSize: 8, color: '#333', marginTop: 1 }}>
                    '{String(prevSeason).slice(-2)} → '{String(currSeason).slice(-2)}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PlayerCareerTab;
